import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Table, Typography, Button, Space, Select, Tooltip, Descriptions } from 'antd';
import { InfoCircleOutlined, FolderAddOutlined, PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import ExpandCollapseAllButton from '../../common/ExpandCollapseAllButton';
import NumberInput from '../../common/NumberInput';
import { useTender } from '../../../contexts/TenderContext';
import { useDevMode } from '@/contexts/DevModeContext';
import { useCanConfigureBoq } from '@/hooks/useCanConfigureBoq';
import { useItemLibrary } from '../../../hooks/useItemLibrary';
import { useBoqLatestVersion } from '../../../hooks/useBillOfQuantities';
import { flattenBoqSummaryLineItems } from '../../../utils/roadsAndSewersBoqSummaryComputer';
import { BcCalculationDetails, computeBcCalculation } from '../../common/BcCalculationDetails';
import type { BoqSummaryLineItem } from '../../../utils/groundworksBoqSummaryComputer';
import { COLORS, formatUnit, MAX_SECTION_NESTING_DEPTH, MAX_SECTION_NESTING_DEPTH_MESSAGE, buildSectionDepthMap } from '../../../utils/constants';
import { formatCurrency } from '../../../utils/calculations';
import { MAIN_SERVICE_TRENCHING_MAPPING, sumMainServicesAmount } from '../../../utils/mainServiceTrenchingMapping';
import { findMainServiceTrenchingEvaluateCode } from '../../../utils/mainServicesEvaluateCodesMapping';
import { useBillSummaryExtraItems, buildRootExtraRows, buildNestedExtraRows } from '../../../hooks/useBillSummaryExtraItems';
import { computeDynamicBcCalculation, computeDynamicBillSummaryNodeAmount, type DynamicBillSummaryLineItem } from '../../../utils/dynamicBillSummarySerialization';
import { useRoadsAndSewersMarkupFactor } from '../../../hooks/useRoadsAndSewersMarkupFactor';
import type { DynamicBillSummaryNode, DynamicBillSummaryItemNode, DynamicBillSummarySectionNode } from '../../../types/tender.types';

const { Text, Title } = Typography;

interface BillRow {
  key: string;
  category: string;
  description: string;
  quantity: number | string;
  unit: string;
  evaluateCode?: string;
  rowType?: 'sub-header' | 'item' | 'main-header' | 'column-type-header' | 'line-item';
  children?: BillRow[];
  sectionAmount?: number;
  // MAIN_SERVICE_TRENCHING_MAPPING's own `item.id` — the key computeMainServicesSection
  // persists this row under (roadAndSewers.summary.mainServices), distinct from this
  // row's own table `key` (`ms-${group.id}-${item.id}`) which is only unique for the
  // <Table>'s rowKey.
  itemId?: string;
  // Ad-hoc extra rows (useBillSummaryExtraItems.tsx).
  isExtra?: boolean;
  attachKey?: string;
  nodeType?: 'section' | 'item';
  amountType?: 'number' | 'text';
  amountText?: string;
  rate?: number | string;
  amount?: number | string;
  dbRate?: string;
}

const isHeaderRowType = (rowType?: string) =>
  rowType === 'sub-header' || rowType === 'main-header' || rowType === 'column-type-header';

const findExtraNode = (nodes: DynamicBillSummaryNode[], id: string): DynamicBillSummaryNode | null => {
  for (const node of nodes) {
    if (node.id === id) return node;
    if (node.type === 'section') {
      const found = findExtraNode(node.children, id);
      if (found) return found;
    }
  }
  return null;
};

const MainServicesBillSummary: React.FC = () => {
  const { state, updateField } = useTender();
  const showDevMode = useDevMode();
  const dynamicSummaryEnabled = useCanConfigureBoq();
  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);
  // SummaryKey modal — "BCCalculation" independent backend-dbRate-only recomputation,
  // same as groundworks Bill Summaries (BcCalculationDetails.tsx / computeBcCalculation).
  const [summaryKeyModalRecord, setSummaryKeyModalRecord] = useState<BoqSummaryLineItem | null>(null);
  const [extraSummaryKeyRecord, setExtraSummaryKeyRecord] = useState<DynamicBillSummaryLineItem | null>(null);

  // Ad-hoc user-added Section/Item rows grafted onto this otherwise-immutable
  // computed table — see useBillSummaryExtraItems.tsx.
  const mainServicesExtraItems = state.mainServicesExtraItems;
  const [newlyAddedRowId, setNewlyAddedRowId] = useState<string | null>(null);
  useEffect(() => {
    if (!newlyAddedRowId) return;
    const timer = setTimeout(() => setNewlyAddedRowId(null), 2000);
    return () => clearTimeout(timer);
  }, [newlyAddedRowId]);

  const { data: itemLibraryData } = useItemLibrary(state.region || '');
  // Same OH&P/Preliminaries markup every R&S Bill Summary applies to its own
  // Rate column now — baked into rateMap below so every downstream lookup
  // (Rate/Amount columns, footer via sumMainServicesAmount) picks it up for
  // free.
  const markupFactor = useRoadsAndSewersMarkupFactor();
  // Rate is rounded to 2dp here — the SAME value shown in the Rate column —
  // so Amount always equals the displayed Rate × displayed Qty exactly (the
  // raw item-library rate can carry more than 2dp, which would otherwise
  // silently drift Amount away from what the user can verify by hand). Same
  // convention every groundworks Bill Summary's Amount column uses.
  const rateMap = useMemo(() => {
    const map = new Map<string, number>();
    itemLibraryData?.forEach((item) => {
      const code = item.resourceCode?.trim().toUpperCase();
      if (code) map.set(code, parseFloat(((item.nettRate || item.baseRate || 0) * markupFactor).toFixed(2)));
    });
    return map;
  }, [itemLibraryData, markupFactor]);
  const getLibRate = (code?: string): number | undefined => {
    if (!code) return undefined;
    return rateMap.get(code.trim().toUpperCase());
  };
  // Plain nett rate, no markup — only for the dev-mode "BC" modal, which
  // shows the backend's dbRate-only recomputation for reconciliation.
  const nettRateMap = useMemo(() => {
    const map = new Map<string, number>();
    itemLibraryData?.forEach((item) => {
      const code = item.resourceCode?.trim().toUpperCase();
      if (code) map.set(code, parseFloat((item.nettRate || item.baseRate || 0).toFixed(2)));
    });
    return map;
  }, [itemLibraryData]);

  const extraItemsApi = useBillSummaryExtraItems({
    extraItems: mainServicesExtraItems,
    onChange: (updated) => updateField('mainServicesExtraItems', updated),
    itemLibraryData,
    enableRunMatrixMapping: true,
    onNodeAdded: (expandKey, nodeId) => {
      if (expandKey) {
        setExpandedRowKeys((prev) => (prev.includes(expandKey) ? prev : [...prev, expandKey]));
      }
      setNewlyAddedRowId(nodeId);
    },
  });

  // Dev-mode only: pulls the persisted `roadAndSewers.summary.mainServices` tree
  // from the same boq-versions API the rest of this review page uses, so the
  // SummaryKey column reflects what was actually saved rather than the live
  // recomputation below.
  const { data: latestBoqVersionData } = useBoqLatestVersion(
    state.projectName || undefined,
    { enabled: showDevMode && !!state.projectName, oppNo: state.opportunityNo },
  );
  const persistedMainServices = useMemo(
    () => flattenBoqSummaryLineItems(
      (latestBoqVersionData as unknown as { boq?: { billOfQuantities?: Array<{ roadAndSewers?: { summary?: { mainServices?: import('../../../utils/groundworksBoqSummaryComputer').BoqSummaryLineItem[] } } }> } })
        ?.boq?.billOfQuantities?.[0]?.roadAndSewers?.summary?.mainServices,
    ),
    [latestBoqVersionData],
  );

  const quantities: Record<string, Record<string, number>> =
    state.mainServices?.table || {};

  // Single flat "MAIN SERVICE TRENCHING" category — matches /roads-sewers/main-services-substation's
  // own take-off table (MainServices.tsx's ALL_ITEMS/SECTION_HEADER_ROW), which flattens every
  // MAIN_SERVICE_TRENCHING_MAPPING group into one list under one section, not per-utility groups.
  const { processedData } = useMemo(() => {
    const items: BillRow[] = [];
    let itemNum = 1;
    let totalAmount = 0;

    MAIN_SERVICE_TRENCHING_MAPPING.forEach((group) => {
      group.items.forEach((item) => {
        const roadQtys = quantities[item.id] || {};
        const totalQty = Object.values(roadQtys).reduce((s, q) => s + (q || 0), 0);
        if (totalQty <= 0) return;

        const evaluateCode = findMainServiceTrenchingEvaluateCode(item.description);
        const rate = getLibRate(evaluateCode);
        if (rate != null) totalAmount += totalQty * rate;

        items.push({
          key: `ms-${group.id}-${item.id}`,
          category: `1.${itemNum}`,
          description: item.description,
          quantity: totalQty,
          unit: item.unit,
          evaluateCode,
          rowType: 'item',
          itemId: item.id,
        });
        itemNum++;
      });
    });

    const rows: BillRow[] = items.length > 0 ? [{
      key: 'group-main-service-trenching',
      category: '1.0',
      description: 'MAIN SERVICE TRENCHING',
      quantity: '',
      unit: '',
      rowType: 'sub-header',
      sectionAmount: totalAmount,
      children: items,
    }] : [];

    // Graft ad-hoc extra rows on top of this otherwise-immutable computed
    // tree — same convention every groundworks Bill Summary follows, marked
    // up the same way this table's own Rate column now is.
    const extraBuckets = mainServicesExtraItems ?? {};
    const consumedAttachKeys = new Set<string>();
    const injectExtraRows = (rowsIn: BillRow[]): BillRow[] =>
      rowsIn.map((row) => {
        const bucket = extraBuckets[row.key];
        const existingChildren: BillRow[] = row.children ?? [];
        const ownChildren = row.children ? injectExtraRows(row.children) : undefined;
        if (!bucket || bucket.length === 0) {
          return ownChildren ? { ...row, children: ownChildren } : row;
        }
        consumedAttachKeys.add(row.key);
        const extraRows = buildNestedExtraRows(bucket, row.key, row.category, existingChildren.length, markupFactor);
        const mergedChildren = [...(ownChildren ?? []), ...extraRows];
        return {
          ...row,
          children: mergedChildren,
          ...(row.sectionAmount !== undefined && {
            sectionAmount: mergedChildren.reduce((sum, c) => sum + (parseFloat(String(c.amount || '0')) || 0), 0),
          }),
        };
      });
    const injectedRows = injectExtraRows(rows);
    let nextRootNumber = rows.length > 0 ? 2 : 1;
    const rootExtraRows = buildRootExtraRows(extraBuckets['__root__'] ?? [], '__root__', nextRootNumber, markupFactor);
    nextRootNumber += rootExtraRows.length;
    const orphanedExtraRows = Object.keys(extraBuckets)
      .filter((key) => key !== '__root__' && !consumedAttachKeys.has(key) && extraBuckets[key].length > 0)
      .map((key) => {
        const n = nextRootNumber++;
        const children = buildNestedExtraRows(extraBuckets[key], key, `${n}.0`, 0, markupFactor);
        return {
          key: `orphaned-${key}`,
          category: `${n}.0`,
          description: 'Additional Items',
          rowType: 'main-header' as const,
          isExtra: true,
          attachKey: key,
          nodeType: 'section' as const,
          quantity: '',
          unit: '',
          dbRate: '',
          sectionAmount: children.reduce((sum, c) => sum + (parseFloat(c.amount || '0') || 0), 0),
          children,
        };
      });
    const finalRows: BillRow[] = [...injectedRows, ...rootExtraRows, ...orphanedExtraRows];

    return { processedData: finalRows };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantities, rateMap, mainServicesExtraItems, markupFactor]);

  const rowDepthMap = useMemo(() => buildSectionDepthMap(processedData), [processedData]);

  const totalAmount = useMemo(() => {
    return sumMainServicesAmount(quantities, (code) => rateMap.get(code.trim().toUpperCase()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quantities, rateMap]);

  const handleExpandCollapseAll = () => {
    if (expandedRowKeys.length > 0) {
      setExpandedRowKeys([]);
    } else {
      const allKeys: React.Key[] = [];
      const collect = (rows: BillRow[]) => {
        rows.forEach((row) => {
          if (row.children?.length) {
            allKeys.push(row.key);
            collect(row.children);
          }
        });
      };
      collect(processedData);
      setExpandedRowKeys(allKeys);
    }
  };

  const columns = [
    {
      title: 'Item',
      dataIndex: 'category',
      key: 'category',
      width: 110,
      align: 'center' as const,
      render: (value: string, record: BillRow) => {
        if (isHeaderRowType(record.rowType)) return <Text strong>{value}</Text>;
        return value;
      },
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      render: (value: string, record: BillRow) => {
        if (isHeaderRowType(record.rowType))
          return <Text strong className="text-indigo-600" style={{ textTransform: 'uppercase' }}>{value}</Text>;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Text>{value}</Text>
            {showDevMode && record.evaluateCode && (
              <div style={{ fontSize: 10, color: '#999', fontWeight: 500, marginTop: 2, fontFamily: 'monospace' }}>
                {record.evaluateCode}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      width: 110,
      align: 'right' as const,
      render: (value: number | string, record: BillRow) => {
        if (isHeaderRowType(record.rowType)) return '';
        if (record.isExtra && record.nodeType === 'item' && record.amountType !== 'text' && dynamicSummaryEnabled && !state.isViewMode) {
          return (
            <NumberInput
              value={parseFloat(String(value)) || 0}
              onChange={(qty) => extraItemsApi.updateNode(record.attachKey!, record.key, { qty })}
              size="small"
              decimalPlaces={2}
              autoFocus={record.key === newlyAddedRowId}
            />
          );
        }
        if (!value && value !== 0) return '';
        const n = typeof value === 'number' ? value : parseFloat(String(value));
        return isNaN(n) ? '' : (
          <div style={{ textAlign: 'right' }}>
            {n.toLocaleString('en-GB', { maximumFractionDigits: 2 })}
          </div>
        );
      },
    },
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: 110,
      align: 'center' as const,
      render: (value: string, record: BillRow) => {
        if (isHeaderRowType(record.rowType)) return '';
        if (record.isExtra && record.nodeType === 'item' && record.amountType !== 'text' && dynamicSummaryEnabled && !state.isViewMode) {
          return (
            <Select
              value={value || undefined}
              onChange={(unit) => extraItemsApi.updateNode(record.attachKey!, record.key, { unit })}
              options={extraItemsApi.unitOptions.map((u) => ({ value: u, label: u }))}
              placeholder="Unit"
              size="small"
              showSearch
              allowClear
              style={{ width: '100%' }}
            />
          );
        }
        return formatUnit(value);
      },
    },
    {
      title: 'Rate',
      key: 'rate',
      width: 110,
      align: 'right' as const,
      render: (_: unknown, record: BillRow) => {
        if (isHeaderRowType(record.rowType)) return '';
        if (record.isExtra) {
          const n = typeof record.rate === 'number' ? record.rate : parseFloat(String(record.rate ?? ''));
          return !isNaN(n) && n !== 0 ? <span>{formatCurrency(n)}</span> : <Text type="secondary">—</Text>;
        }
        const rate = getLibRate(record.evaluateCode);
        return rate != null ? <span>{formatCurrency(rate)}</span> : <Text type="secondary">—</Text>;
      },
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 120,
      align: 'right' as const,
      render: (_: unknown, record: BillRow) => {
        if (isHeaderRowType(record.rowType)) {
          const isExpanded = record.rowType !== 'sub-header' || expandedRowKeys.includes(record.key);
          if (!isExpanded && record.sectionAmount && record.sectionAmount > 0) {
            return <Text strong>{formatCurrency(record.sectionAmount)}</Text>;
          }
          return '';
        }
        if (record.amountType === 'text') {
          return <span>{record.amountText}</span>;
        }
        if (record.isExtra) {
          const n = typeof record.amount === 'number' ? record.amount : parseFloat(String(record.amount ?? ''));
          return !isNaN(n) && n !== 0 ? <span style={{ fontWeight: 600 }}>{formatCurrency(n)}</span> : <Text type="secondary">—</Text>;
        }
        const rate = getLibRate(record.evaluateCode);
        if (rate == null) return <Text type="secondary">—</Text>;
        const qty = typeof record.quantity === 'number' ? record.quantity : 0;
        return <span style={{ fontWeight: 600 }}>{formatCurrency(qty * rate)}</span>;
      },
    },
    // ── Dev-mode only: persisted-vs-live comparison column ─────────────────────
    ...(showDevMode ? [{
      title: 'SummaryKey',
      key: 'summaryKey',
      width: 120,
      align: 'right' as const,
      render: (_: unknown, record: BillRow) => {
        if (isHeaderRowType(record.rowType)) return '';
        if (record.isExtra) {
          const node = findExtraNode(mainServicesExtraItems?.[record.attachKey!] ?? [], record.key) as DynamicBillSummaryItemNode | null;
          if (!node) return '';
          const liveAmount = typeof record.amount === 'number' ? record.amount : parseFloat(String(record.amount ?? '0')) || 0;
          const lineItem: DynamicBillSummaryLineItem = {
            key: node.id, description: node.description, quantity: node.qty, unit: node.unit, rate: node.rate,
            amount: liveAmount, amountType: node.amountType, amountText: node.amountText,
            resourceCode: node.resourceCode, resourceCodes: node.resourceCodes,
          };
          const bc = computeDynamicBcCalculation(lineItem, (code) => nettRateMap.get(code) ?? 0);
          return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Text style={{ fontSize: 12, color: '#f59e0b' }}>unsaved</Text>
                <InfoCircleOutlined style={{ cursor: 'pointer', color: '#3b82f6' }} onClick={() => setExtraSummaryKeyRecord(lineItem)} />
              </div>
              {bc && <Text style={{ fontSize: 10, color: '#3b82f6' }}>BC: {formatCurrency(bc.amount)}</Text>}
            </div>
          );
        }
        const persisted = record.itemId ? persistedMainServices.get(record.itemId) : undefined;
        if (!persisted) return <Text style={{ fontSize: 10, color: '#f59e0b' }}>unsaved</Text>;
        const rate = getLibRate(record.evaluateCode);
        const qty = typeof record.quantity === 'number' ? record.quantity : 0;
        const liveAmount = rate != null ? qty * rate : 0;
        const mismatch = Math.abs(liveAmount - (persisted.amount || 0)) > 0.01;
        const bcAmount = computeBcCalculation(persisted, (code) => nettRateMap.get(code) ?? 0).amount;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Text style={{ fontSize: 12, color: mismatch ? '#dc2626' : '#6b7280' }}>
                {formatCurrency(persisted.amount || 0)}
              </Text>
              <InfoCircleOutlined
                style={{ cursor: 'pointer', color: '#3b82f6' }}
                onClick={() => setSummaryKeyModalRecord(persisted)}
              />
            </div>
            <Text style={{ fontSize: 10, color: mismatch ? '#dc2626' : '#3b82f6' }}>
              BC: {formatCurrency(bcAmount)}
            </Text>
          </div>
        );
      },
    }] : []),
    ...(dynamicSummaryEnabled && !state.isViewMode ? [{
      title: 'Actions',
      key: 'extraActions',
      width: 150,
      align: 'center' as const,
      render: (_: unknown, record: BillRow) => {
        if (record.isExtra) {
          const node = findExtraNode(mainServicesExtraItems?.[record.attachKey!] ?? [], record.key);
          if (!node) return '';
          if (record.nodeType === 'section') {
            return (
              <Space size={4}>
                <Tooltip title={(rowDepthMap.get(record.key) ?? 0) >= MAX_SECTION_NESTING_DEPTH ? MAX_SECTION_NESTING_DEPTH_MESSAGE : undefined}>
                  <Button size="small" icon={<FolderAddOutlined />} title="Add Section" disabled={(rowDepthMap.get(record.key) ?? 0) >= MAX_SECTION_NESTING_DEPTH} onClick={() => extraItemsApi.openAddSection(record.attachKey!, record.key)} />
                </Tooltip>
                <Button size="small" icon={<PlusOutlined />} title="Add Item" onClick={() => extraItemsApi.openAddItem(record.attachKey!, record.key)} />
                <Button size="small" icon={<EditOutlined />} title="Edit Section" onClick={() => extraItemsApi.openEditSection(record.attachKey!, node as DynamicBillSummarySectionNode)} />
                <Button size="small" icon={<DeleteOutlined />} danger title="Delete" onClick={() => extraItemsApi.requestDelete(record.attachKey!, record.key)} />
              </Space>
            );
          }
          return (
            <Space size={4}>
              <Button size="small" icon={<EditOutlined />} title="Edit Item" onClick={() => extraItemsApi.openEditItem(record.attachKey!, node as DynamicBillSummaryItemNode)} />
              <Button size="small" icon={<DeleteOutlined />} danger title="Delete" onClick={() => extraItemsApi.requestDelete(record.attachKey!, record.key)} />
            </Space>
          );
        }
        if (!isHeaderRowType(record.rowType)) return '';
        const atMaxDepth = (rowDepthMap.get(record.key) ?? 0) >= MAX_SECTION_NESTING_DEPTH;
        return (
          <Space size={4}>
            <Tooltip title={atMaxDepth ? MAX_SECTION_NESTING_DEPTH_MESSAGE : undefined}>
              <Button size="small" icon={<FolderAddOutlined />} title="Add Section" disabled={atMaxDepth} onClick={() => extraItemsApi.openAddSection(record.key, null)} />
            </Tooltip>
            <Button size="small" icon={<PlusOutlined />} title="Add Item" onClick={() => extraItemsApi.openAddItem(record.key, null)} />
          </Space>
        );
      },
    }] : []),
  ];

  const hasData = processedData.length > 0;
  // Extra items merge into `processedData` above but not into the separately
  // computed `totalAmount` (sumMainServicesAmount reads live quantities, not
  // processedData) — add their own total here so the footer stays consistent
  // with what's on screen, marked up the same way as every other domain's
  // extra items (previously a bespoke un-marked-up, non-recursive reduction —
  // now the same shared `computeDynamicBillSummaryNodeAmount` used
  // everywhere else, which also correctly handles sections nested more than
  // one level deep).
  const extraItemsFooterTotal = useMemo(
    () => Object.values(mainServicesExtraItems ?? {}).reduce(
      (sum, nodes) => sum + nodes.reduce((s, n) => s + computeDynamicBillSummaryNodeAmount(n, markupFactor), 0),
      0,
    ),
    [mainServicesExtraItems, markupFactor],
  );

  return (
    <div style={{ padding: '16px' }}>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Title level={5} style={{ margin: 0, fontWeight: 600, fontSize: '16px', color: '#111827' }}>
          Main Services Bill Summary
        </Title>
        <Space>
          {dynamicSummaryEnabled && !state.isViewMode && (
            <Button size="small" icon={<FolderAddOutlined />} onClick={() => extraItemsApi.openAddSection('__root__', null)}>
              Add Section
            </Button>
          )}
          <ExpandCollapseAllButton expanded={expandedRowKeys.length > 0} onToggle={handleExpandCollapseAll} />
        </Space>
      </div>
      <style>{`
        .main-services-bill-table .ant-table { border-radius: 0 !important; }
        .main-services-bill-table .ant-table-container { border-radius: 0 !important; }
        .main-services-bill-table .ant-table-thead > tr > th { background-color: ${COLORS.tableHeaderBg} !important; }
        /* Explicit :hover so every row visibly highlights on hover, same fix
           as FoulSewersBillSummary.tsx/StormSewersBillSummary.tsx — plain CSS
           :hover applies regardless of any legacy colSpan render props a cell
           might use, unlike antd's own JS-driven hover class. */
        .main-services-bill-table .ant-table-tbody > tr:hover > td { background-color: #fafafa !important; }
        .main-services-bill-table .ant-table-tbody > tr.bg-indigo-50:hover > td { background-color: #e0e7ff !important; }
        .main-services-bill-table .ant-table-tbody > tr.bg-gray-50:hover > td { background-color: #f3f4f6 !important; }
      `}</style>
      {hasData ? (
        <Table
          className="main-services-bill-table"
          dataSource={processedData}
          columns={columns}
          rowKey="key"
          bordered
          size="middle"
          expandable={{
            childrenColumnName: 'children',
            defaultExpandAllRows: false,
            expandedRowKeys,
            onExpand: (expanded, record) => {
              setExpandedRowKeys(
                expanded
                  ? [...expandedRowKeys, record.key]
                  : expandedRowKeys.filter((k) => k !== record.key),
              );
            },
          }}
          rowClassName={(record) => {
            const classes: string[] = [];
            if (record.rowType === 'sub-header' || record.rowType === 'main-header') classes.push('bg-indigo-50', 'font-semibold');
            else if (record.rowType === 'column-type-header') classes.push('bg-gray-50', 'font-semibold');
            if (record.key === newlyAddedRowId) classes.push('main-services-summary-row-added');
            return classes.join(' ');
          }}
          pagination={false}
          summary={() => (
            <Table.Summary.Row style={{ backgroundColor: '#fafafa', fontWeight: 700 }}>
              <Table.Summary.Cell index={0} colSpan={2}>
                <Text strong style={{ fontWeight: 700 }}>TOTAL MAIN SERVICES</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={2} />
              <Table.Summary.Cell index={3} />
              <Table.Summary.Cell index={4} />
              <Table.Summary.Cell index={5} align="right">
                <span style={{ display: 'block', textAlign: 'right', fontWeight: 700 }}>{formatCurrency(totalAmount + extraItemsFooterTotal)}</span>
              </Table.Summary.Cell>
              {showDevMode && <Table.Summary.Cell index={6} />}
              {dynamicSummaryEnabled && !state.isViewMode && <Table.Summary.Cell index={showDevMode ? 7 : 6} />}
            </Table.Summary.Row>
          )}
        />
      ) : (
        <div style={{ padding: '32px', textAlign: 'center', color: '#9ca3af' }}>
          <Text type="secondary">No main service quantities entered. Enter quantities on the Main Services page.</Text>
        </div>
      )}
      <style>{`
        .main-services-bill-table .ant-table-thead > tr > th { min-width: 110px; }
        @keyframes main-services-summary-row-added-fade { 0% { background-color: #fef3c7; } 100% { background-color: transparent; } }
        .main-services-summary-row-added > td { animation: main-services-summary-row-added-fade 1.8s ease-out; }
      `}</style>
      <Modal
        title="SummaryKey — Amount Calculation"
        open={!!summaryKeyModalRecord}
        onCancel={() => setSummaryKeyModalRecord(null)}
        footer={null}
      >
        {summaryKeyModalRecord && (
          <BcCalculationDetails
            record={summaryKeyModalRecord}
            getDbRate={(code) => nettRateMap.get(code) ?? 0}
          />
        )}
      </Modal>

      <Modal
        title="SummaryKey — Live Value (not yet in BOQ export)"
        open={!!extraSummaryKeyRecord}
        onCancel={() => setExtraSummaryKeyRecord(null)}
        footer={null}
      >
        {extraSummaryKeyRecord && (() => {
          const bc = computeDynamicBcCalculation(extraSummaryKeyRecord, (code) => nettRateMap.get(code) ?? 0);
          return (
            <>
              <Descriptions bordered size="small" column={1}>
                <Descriptions.Item label="Description">{extraSummaryKeyRecord.description}</Descriptions.Item>
                {extraSummaryKeyRecord.quantity != null && <Descriptions.Item label="Quantity">{extraSummaryKeyRecord.quantity}</Descriptions.Item>}
                {extraSummaryKeyRecord.unit && <Descriptions.Item label="Unit">{formatUnit(extraSummaryKeyRecord.unit)}</Descriptions.Item>}
                {extraSummaryKeyRecord.rate != null && <Descriptions.Item label="Rate">{formatCurrency(extraSummaryKeyRecord.rate)}</Descriptions.Item>}
                {extraSummaryKeyRecord.resourceCode && <Descriptions.Item label="Resource Code">{extraSummaryKeyRecord.resourceCode}</Descriptions.Item>}
                <Descriptions.Item label="Amount">{formatCurrency(extraSummaryKeyRecord.amount)}</Descriptions.Item>
              </Descriptions>
              {bc && (
                <div style={{ marginTop: 8, padding: 8, background: '#eff6ff', borderRadius: 4 }}>
                  <Text strong>BCCalculation (backend dbRate-only derivation):</Text>
                  <div style={{ marginTop: 8 }}>
                    {bc.steps.map((step, idx) => <div key={idx}><Text style={{ fontSize: 12, color: '#374151' }}>{step}</Text></div>)}
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </Modal>

      {extraItemsApi.modals}
    </div>
  );
};

export default MainServicesBillSummary;
